import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SeoHead } from '../components/SeoHead';
import { Check, ExternalLink, Search, X, Send, ChevronRight, Shield, Clock, Zap, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { id: 'tutti', label: 'Tutti' },
  { id: 'pms', label: 'PMS' },
  { id: 'automazione', label: 'Automazione' },
  { id: 'canali', label: 'Canali OTA' },
  { id: 'accesso', label: 'Controllo Accesso' },
  { id: 'contabilita', label: 'Contabilità' },
  { id: 'comunicazione', label: 'Comunicazione' },
];

const DIFFICULTY_LABEL = { easy: 'Facile', medium: 'Intermedio', advanced: 'Avanzato' };
const DIFFICULTY_COLOR = { easy: '#22c55e', medium: '#f59e0b', advanced: '#ef4444' };

// Componente stelle (sola lettura)
function Stars({ value = 0, size = 12 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={size}
          fill={value >= i ? '#c9a84c' : value >= i - 0.5 ? 'url(#half)' : 'none'}
          stroke={value >= i - 0.5 ? '#c9a84c' : 'rgba(229,229,229,0.2)'} />
      ))}
    </span>
  );
}

function IntegrationCard({ item, onSelect }) {
  return (
    <button
      data-testid={`marketplace-card-${item.integration_id}`}
      onClick={() => onSelect(item)}
      className="text-left w-full p-5 rounded-sm transition-all hover:translate-y-[-2px]"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer' }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
          style={{ background: item.logo_color }}>
          {item.logo_letter}
        </div>
        <div className="flex gap-1.5 flex-wrap justify-end">
          {item.certified && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-sm"
              style={{ background: 'rgba(201,168,76,0.12)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.25)' }}>
              <Shield size={9} /> Certificato
            </span>
          )}
          {item.featured && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-sm"
              style={{ background: 'rgba(229,212,161,0.12)', color: '#e5d4a1', border: '1px solid rgba(229,212,161,0.25)' }}>
              <Star size={9} /> In evidenza
            </span>
          )}
        </div>
      </div>
      <p className="font-medium text-sm mb-1" style={{ color: '#e5e5e5' }}>{item.name}</p>
      <p className="text-xs mb-3" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.5 }}>{item.tagline}</p>
      <div className="flex items-center gap-3 text-[10px]" style={{ color: 'rgba(229,229,229,0.35)' }}>
        <span className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: DIFFICULTY_COLOR[item.setup_difficulty] }} />
          {DIFFICULTY_LABEL[item.setup_difficulty]}
        </span>
        <span className="flex items-center gap-1">
          <Clock size={9} /> ~{item.setup_time_min} min
        </span>
        {item.review_count > 0 && (
          <span className="flex items-center gap-1 ml-auto">
            <Stars value={item.avg_rating} size={10} />
            <span style={{ color: '#c9a84c' }}>{item.avg_rating}</span>
            <span>({item.review_count})</span>
          </span>
        )}
      </div>
    </button>
  );
}

function IntegrationModal({ item, onClose }) {
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    if (!item) return;
    setLoadingReviews(true);
    axios.get(`${API}/api/marketplace/${item.integration_id}/reviews`)
      .then(r => setReviews(r.data.reviews || []))
      .catch(() => {})
      .finally(() => setLoadingReviews(false));
  }, [item]);

  if (!item) return null;

  const avgRating = item.avg_rating || (reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0);
  const reviewCount = item.review_count || reviews.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}>
      <div className="w-full max-w-lg rounded-sm overflow-hidden max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}
        style={{ background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="p-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-sm flex items-center justify-center font-bold text-white"
                style={{ background: item.logo_color }}>{item.logo_letter}</div>
              <div>
                <p className="font-medium" style={{ color: '#e5e5e5' }}>{item.name}</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {item.certified && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c' }}>Certificato Shangrilux</span>}
                  <span className="text-[10px] px-1.5 py-0.5 rounded capitalize" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(229,229,229,0.5)' }}>{item.category}</span>
                </div>
                {reviewCount > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <Stars value={avgRating} size={13} />
                    <span className="text-xs" style={{ color: 'rgba(229,229,229,0.5)' }}>
                      {avgRating.toFixed(1)} su 5 · {reviewCount} {reviewCount === 1 ? 'recensione' : 'recensioni'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} style={{ color: 'rgba(229,229,229,0.4)', background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={18} />
            </button>
          </div>
          <p className="text-sm" style={{ color: 'rgba(229,229,229,0.6)', lineHeight: 1.7 }}>{item.description}</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="p-3 rounded-sm" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: 'rgba(229,229,229,0.3)' }}>Difficoltà setup</p>
              <p className="text-sm font-medium" style={{ color: DIFFICULTY_COLOR[item.setup_difficulty] }}>{DIFFICULTY_LABEL[item.setup_difficulty]}</p>
            </div>
            <div className="p-3 rounded-sm" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: 'rgba(229,229,229,0.3)' }}>Tempo di configurazione</p>
              <p className="text-sm font-medium" style={{ color: '#e5e5e5' }}>~{item.setup_time_min} minuti</p>
            </div>
          </div>
          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-5">
              {item.tags.map(t => (
                <span key={t} className="px-2 py-0.5 text-[10px] rounded-sm" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(229,229,229,0.4)' }}>{t}</span>
              ))}
            </div>
          )}

          {/* Sezione recensioni */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-widest" style={{ color: 'rgba(229,229,229,0.3)' }}>
                Recensioni hotel ({reviewCount})
              </p>
              <a href="/panel" target="_blank"
                className="text-[10px] hover:opacity-80 transition-opacity"
                style={{ color: '#c9a84c', textDecoration: 'none' }}>
                + Lascia la tua recensione →
              </a>
            </div>
            {loadingReviews ? (
              <div className="flex justify-center py-4">
                <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: '#c9a84c', borderTopColor: 'transparent' }} />
              </div>
            ) : reviews.length === 0 ? (
              <p className="text-xs py-3 text-center" style={{ color: 'rgba(229,229,229,0.25)' }}>
                Nessuna recensione ancora. Sii il primo!
              </p>
            ) : (
              <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                {reviews.slice(0, 8).map((r, i) => (
                  <div key={i} className="rounded-sm overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <Stars value={r.rating} size={11} />
                          <span className="text-[10px] font-medium" style={{ color: 'rgba(229,229,229,0.6)' }}>{r.tenant_name}</span>
                        </div>
                        <span className="text-[9px]" style={{ color: 'rgba(229,229,229,0.2)' }}>
                          {new Date(r.updated_at || r.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                      {r.comment && <p className="text-xs italic" style={{ color: 'rgba(229,229,229,0.45)', lineHeight: 1.5 }}>"{r.comment}"</p>}
                    </div>
                    {/* Risposta Team Shangrilux */}
                    {r.admin_reply && (
                      <div className="px-3 py-2.5 flex gap-2" style={{ background: 'rgba(201,168,76,0.06)', borderTop: '1px solid rgba(201,168,76,0.12)' }}>
                        <div className="w-4 h-4 rounded flex items-center justify-center text-[8px] font-bold flex-shrink-0 mt-0.5" style={{ background: '#c9a84c', color: '#000' }}>O</div>
                        <div>
                          <p className="text-[9px] font-semibold mb-0.5" style={{ color: '#c9a84c' }}>Team Shangrilux</p>
                          <p className="text-[10px]" style={{ color: 'rgba(229,229,229,0.55)', lineHeight: 1.5 }}>{r.admin_reply}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            {item.website && (
              <a href={item.website} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
                style={{ background: '#c9a84c', color: '#000', textDecoration: 'none' }}>
                Visita sito <ExternalLink size={13} />
              </a>
            )}
            <a href="/panel" target="_blank"
              className="flex items-center gap-2 px-4 py-2 text-sm"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#e5e5e5', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.1)' }}>
              Configura in Shangrilux <ChevronRight size={13} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function CandidacyForm() {
  const [form, setForm] = useState({ company_name: '', contact_email: '', contact_name: '', integration_name: '', category: 'automazione', website: '', description: '', why_certified: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_email || !form.integration_name) { toast.error('Compila tutti i campi obbligatori.'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/marketplace/candidacy`, form);
      setSent(true);
      toast.success('Candidatura inviata! Ti risponderemo entro 5 giorni lavorativi.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'invio.');
    } finally { setSending(false); }
  };

  if (sent) return (
    <div className="text-center py-12">
      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
        <Check size={24} style={{ color: '#22c55e' }} />
      </div>
      <p className="text-lg font-medium mb-2" style={{ color: '#e5e5e5' }}>Candidatura inviata!</p>
      <p className="text-sm" style={{ color: 'rgba(229,229,229,0.5)' }}>Il nostro team esaminerà la tua richiesta entro 5 giorni lavorativi.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { k: 'company_name', label: 'Nome azienda *', placeholder: 'Acme S.r.l.' },
          { k: 'contact_name', label: 'Nome referente *', placeholder: 'Mario Rossi' },
          { k: 'contact_email', label: 'Email *', placeholder: 'mario@acme.it', type: 'email' },
          { k: 'website', label: 'Sito web', placeholder: 'https://acme.it' },
          { k: 'integration_name', label: 'Nome integrazione *', placeholder: 'Acme Connect' },
        ].map(({ k, label, placeholder, type = 'text' }) => (
          <div key={k}>
            <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.5)' }}>{label}</label>
            <input type={type} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder}
              className="w-full px-3 py-2 text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e5e5' }} />
          </div>
        ))}
        <div>
          <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.5)' }}>Categoria *</label>
          <select value={form.category} onChange={e => set('category', e.target.value)}
            className="w-full px-3 py-2 text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e5e5' }}>
            {CATEGORIES.filter(c => c.id !== 'tutti').map(c => <option key={c.id} value={c.id} style={{ background: '#111' }}>{c.label}</option>)}
          </select>
        </div>
      </div>
      {[
        { k: 'description', label: 'Descrizione breve dell\'integrazione *', placeholder: 'Cosa fa la tua integrazione con Shangrilux?' },
        { k: 'why_certified', label: 'Perché meriti il badge Certificato Shangrilux? *', placeholder: 'Descrivici la tua integrazione, i test effettuati, i clienti che già la usano...' },
      ].map(({ k, label, placeholder }) => (
        <div key={k}>
          <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.5)' }}>{label}</label>
          <textarea rows={3} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder}
            className="w-full px-3 py-2 text-sm outline-none resize-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e5e5' }} />
        </div>
      ))}
      <button type="submit" disabled={sending}
        className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium"
        style={{ background: '#c9a84c', color: '#000', border: 'none', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
        {sending ? 'Invio in corso...' : <><Send size={14} /> Invia candidatura</>}
      </button>
    </form>
  );
}

export default function MarketplacePage() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('tutti');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showCandidacy, setShowCandidacy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/marketplace`)
      .then(r => setIntegrations(r.data.integrations || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = integrations.filter(item => {
    const matchCat = category === 'tutti' || item.category === category;
    const s = search.toLowerCase();
    const matchSearch = !s || item.name.toLowerCase().includes(s) || item.tagline?.toLowerCase().includes(s) || item.tags?.some(t => t.includes(s));
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen" style={{ background: '#080808', color: '#e5e5e5', fontFamily: "'Inter', sans-serif" }}>
      <SeoHead title="Marketplace Integrazioni — Shangrilux" description="Scopri le integrazioni certificate Shangrilux per PMS, automazione, OTA, controllo accessi e contabilità." />

      {/* Nav */}
      <nav className="border-b px-4 sm:px-8 py-4 flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          <span className="font-serif text-lg tracking-widest uppercase" style={{ color: '#c9a84c' }}>Shangrilux</span>
        </a>
        <div className="flex items-center gap-3 sm:gap-6 text-sm" style={{ color: 'rgba(229,229,229,0.5)' }}>
          <a href="/marketplace" className="hidden sm:block" style={{ color: '#c9a84c', textDecoration: 'none', fontWeight: 500 }}>Marketplace</a>
          <a href="/blog" className="hidden sm:block" style={{ color: 'inherit', textDecoration: 'none' }}>Guide</a>
          <a href="/developers" className="hidden sm:block" style={{ color: 'inherit', textDecoration: 'none' }}>Developers</a>
          <a href="/#demo" className="px-4 py-1.5 text-xs" style={{ background: '#c9a84c', color: '#000', textDecoration: 'none' }}>Demo Gratuita</a>
        </div>
      </nav>

      {/* Hero */}
      <div className="px-8 py-16 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full text-xs"
          style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}>
          <Shield size={11} /> Marketplace Ufficiale Shangrilux
        </div>
        <h1 className="font-serif text-4xl font-light mb-4" style={{ color: '#e5e5e5', letterSpacing: '-0.01em' }}>
          Integrazioni Certificate
        </h1>
        <p className="text-base mb-8" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.7 }}>
          Collega Shangrilux ai migliori strumenti per la gestione alberghiera. Ogni integrazione è testata, documentata e garantita dal team Shangrilux.
        </p>

        {/* Search */}
        <div className="relative max-w-md mx-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(229,229,229,0.3)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca integrazione..."
            className="w-full pl-9 pr-4 py-2.5 text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e5e5' }} />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ background: 'none', border: 'none', color: 'rgba(229,229,229,0.4)', cursor: 'pointer' }}><X size={13} /></button>}
        </div>
      </div>

      {/* Categoria filter */}
      <div className="px-8 mb-8 flex flex-wrap gap-2 justify-center">
        {CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setCategory(cat.id)}
            data-testid={`filter-${cat.id}`}
            className="px-4 py-1.5 text-xs transition-all"
            style={{
              background: category === cat.id ? '#c9a84c' : 'rgba(255,255,255,0.04)',
              color: category === cat.id ? '#000' : 'rgba(229,229,229,0.5)',
              border: `1px solid ${category === cat.id ? '#c9a84c' : 'rgba(255,255,255,0.08)'}`,
              fontWeight: category === cat.id ? 600 : 400,
              cursor: 'pointer',
            }}>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Grid integrazioni */}
      <div className="px-8 max-w-6xl mx-auto mb-20">
        {loading ? (
          <div className="text-center py-16" style={{ color: 'rgba(229,229,229,0.3)' }}>
            <div className="w-6 h-6 border-2 rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#c9a84c', borderTopColor: 'transparent' }} />
            Caricamento integrazioni...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p style={{ color: 'rgba(229,229,229,0.4)' }}>Nessuna integrazione trovata per "{search}"</p>
          </div>
        ) : (
          <>
            <p className="text-xs mb-4" style={{ color: 'rgba(229,229,229,0.3)' }}>{filtered.length} integrazioni disponibili</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(item => <IntegrationCard key={item.integration_id} item={item} onSelect={setSelected} />)}
            </div>
          </>
        )}
      </div>

      {/* Candidatura partner */}
      <div className="px-8 py-16 border-t" style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.015)' }}>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl font-light mb-3" style={{ color: '#e5e5e5' }}>
              Sei un partner tecnologico?
            </h2>
            <p className="text-sm" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.7 }}>
              Candidati per ottenere il badge <strong style={{ color: '#c9a84c' }}>Certificato Shangrilux</strong> e far apparire la tua integrazione in questo marketplace.
              Il nostro team valuterà la candidatura entro 5 giorni lavorativi.
            </p>
          </div>
          {!showCandidacy ? (
            <div className="text-center">
              <button onClick={() => setShowCandidacy(true)}
                data-testid="open-candidacy-form"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium"
                style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)', cursor: 'pointer' }}>
                <Zap size={15} /> Candidati come partner
              </button>
            </div>
          ) : (
            <CandidacyForm />
          )}
        </div>
      </div>

      {selected && <IntegrationModal item={selected} onClose={() => setSelected(null)} />}

      {/* Footer */}
      <div className="border-t py-10" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
        <div className="max-w-6xl mx-auto px-8 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '16px', fontWeight: 300, color: 'rgba(229,229,229,0.3)' }}>Shangrilux</span>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/', 'Home'], ['/pricing', 'Prezzi'], ['/blog', 'Guide'], ['/developers', 'API']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.3)' }}>{l}</a>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/privacy', 'Privacy'], ['/cookie', 'Cookie'], ['/terms', 'Termini']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.25)' }}>{l}</a>
            ))}
          </div>
          <p className="text-xs" style={{ color: 'rgba(229,229,229,0.2)' }}>
            © {new Date().getFullYear()} Shangrilux · Design by{' '}
            <a href="https://francescorusso.work" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Francesco Russo</a>
            {' · '}
            <a href="https://shangrilux.com" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Shangrilux</a>
          </p>
        </div>
      </div>
    </div>
  );
}
